import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { FormLabelTranslationService } from '~shared/components/form-fields/translations/form-label-translations.service';
import { noWhitespaceCharacters } from '~shared/directives/form-validators';
import { Attribute, Club } from '~core/models';
import { BasicStatusType, EdsColorType, NotificationType } from '~core/enum';
import { AttributeService, ClubService, DefaultNotificationService, ExportService } from '~core/services';
import { BasicStatusTranslationService, ClubTranslationsService } from '~shared/translations';

/**
 * The create/edit form component for club-overview attributes
 */
@UntilDestroy()
@Component({
  selector: 'app-club-attributes-form',
  templateUrl: './club-attributes-form.component.html'
})
export class ClubAttributesFormComponent implements OnInit, OnDestroy {
  @Input() public selectedClub: Club;
  @Output() public setSubmitLoading: EventEmitter<boolean> = new EventEmitter<boolean>();
  public attributeForm: UntypedFormGroup;
  public attributes: any;
  public selectedAttribute: Attribute;
  public lingerData: boolean = false;
  public updateDataLoading: boolean = false;
  public yesOrNoDropdown: Array<any> = [];

  constructor(
    public clubService: ClubService,
    public attributeService: AttributeService,
    public exportService: ExportService,
    public basicStatusTranslationService: BasicStatusTranslationService,
    public notificationService: DefaultNotificationService,
    public flTranslations: FormLabelTranslationService,
    private clubTranslations: ClubTranslationsService,
    private formBuilder: UntypedFormBuilder
  ) {
    this.yesOrNoDropdown.push(
      { value: true, status: this.flTranslations.i18nActivated },
      { value: false, status: this.flTranslations.i18nDeactivated },
    );
  }

  public ngOnInit(): void {
    this.attributeService.castClubAttributes
      .pipe(untilDestroyed(this))
      .subscribe((att: any) => this.attributes = att);
    this.attributeService.castCreateAttribute
      .pipe(untilDestroyed(this))
      .subscribe((data: any) => {
        if (data !== null) {
          this.fillCreateAttributeForm();
        }
      });
    this.attributeService.castEditAttribute
      .pipe(untilDestroyed(this))
      .subscribe((data: any) => {
        if (data !== null) {
          this.selectedAttribute = data;
          this.fillEditAttributeForm();
        }
      });
  }

  /**
   * Closes workspace sidebar
   */
  public ngOnDestroy(): void {
    this.attributeService.toggleAllModals(false);
    this.attributeService.announceCreateAttribute(null);
    this.attributeService.announceEditAttribute(null);
  }

  /**
   * makes the field required if the predicate function returns true
   */
  public requiredIfValidator(predicate) {
    return (formControl => {
      if (!formControl.parent) {
        return null;
      }
      if (predicate()) {
        return Validators.required(formControl);
      }
      return null;
    });
  }

  /**
   * Fills the attribute form
   */
  public fillCreateAttributeForm(): void {
    if (!this.lingerData) {
      this.attributeForm = this.formBuilder.group({
        name: ['', [
          Validators.required,
          Validators.maxLength(25),
          noWhitespaceCharacters
        ]],
        description: ['', [
          Validators.maxLength(255),
          noWhitespaceCharacters
        ]],
        status: [true],
        club: [`/api/clubs/${this.selectedClub.id}`],
        color: [EdsColorType.SUCCESS, [
          Validators.required
        ]],
        syncTixxPool: [false],
        assignable: [true],
        customerPoolGroupId: [null, [
          this.requiredIfValidator(() => this.attributeForm.get('syncTixxPool').value),
          Validators.maxLength(9),
          Validators.pattern('^(0|[1-9][0-9]*)$')
        ]]
      });
    }
    this.lingerData = true;
  }

  /**
   * Fills the attribute form
   */
  public fillEditAttributeForm(): void {
    this.lingerData = false;
    const dataPool = this.selectedClub.isDataFeedActive ? null : this.selectedAttribute.customerPoolGroupId;

    this.attributeForm = this.formBuilder.group({
      name: [{ value: this.selectedAttribute.name, disabled: this.selectedAttribute.internal }, [
        Validators.required,
        Validators.maxLength(25),
        noWhitespaceCharacters
      ]],
      description: [this.selectedAttribute.description, [
        Validators.maxLength(255),
        noWhitespaceCharacters
      ]],
      status: [{ value: this.selectedAttribute.isChecked, disabled: this.selectedAttribute.internal }],
      club: [`/api/clubs/${this.selectedClub.id}`],
      color: [this.selectedAttribute.color, [
        Validators.required
      ]],
      syncTixxPool: [dataPool ? true : false],
      assignable: [{ value: this.selectedAttribute.assignable, disabled: this.selectedAttribute.internal }],
      customerPoolGroupId: [dataPool, [
        this.requiredIfValidator(() => this.attributeForm.get('syncTixxPool').value),
        Validators.maxLength(9),
        Validators.pattern('^(0|[1-9][0-9]*)$')
      ]]
    });
  }

  /**
   * Submit the attribute create form if its valid, otherwise display errors.
   */
  public submitAttributeCreate(form: UntypedFormGroup): void {
    const formValue = form.getRawValue();
    if (form.valid) {
      this.setSubmitLoading.emit(true);
      const customerPoolId = form.get('customerPoolGroupId');
      if (customerPoolId.value) {
        customerPoolId.setValue(parseFloat(customerPoolId.value));
      } else {
        customerPoolId.setValue(null);
      }

      const statusString = this.getStatusString(formValue.status);
      formValue.status = statusString;
      delete formValue.syncTixxPool;

      this.attributeService.createClubAttribute(formValue)
        .pipe(untilDestroyed(this))
        .subscribe({
          next: (res: any) => {
            form.reset();
            this.attributeService.addClubAttributeToList(res);
            this.attributeService.toggleCreateAttributeModal(false);
            this.setSubmitLoading.emit(false);
            this.lingerData = false;
          },
          error: (error: Error) => {
            this.setSubmitLoading.emit(false);
            this.notificationService.showNotification({
              type: NotificationType.ALERT,
              message: this.clubTranslations.i18nErrorAttributeCreate
            });
          }
        });
    }
  }

  /**
   * Submit the attribute create form if its valid, otherwise display errors.
   */
  public submitAttributeEdit(form: UntypedFormGroup): void {
    const formValue = form.getRawValue();

    if (form.valid) {
      this.setSubmitLoading.emit(true);

      const customerPoolId = form.get('customerPoolGroupId');
      if (customerPoolId.value) {
        customerPoolId.setValue(parseFloat(customerPoolId.value));
      } else {
        customerPoolId.setValue(null);
      }

      const statusString = this.getStatusString(formValue.status);
      formValue.status = statusString;
      delete formValue.syncTixxPool;


      this.attributeService.updateClubAttribute(formValue, this.selectedAttribute.id)
        .pipe(untilDestroyed(this))
        .subscribe({
          next: (res: any) => {
            form.reset();
            this.attributeService.updateClubAttributeToList(res);
            this.attributeService.announceEditAttribute(null);
            this.attributeService.toggleEditAttributeModal(false);
            this.setSubmitLoading.emit(false);
            this.lingerData = false;
          },
          error: (error: Error) => {
            this.setSubmitLoading.emit(false);
            this.notificationService.showNotification({
              type: NotificationType.ALERT,
              message: this.clubTranslations.i18nErrorAttributeUpdate
            });
          }
        });
    }
  }

  /**
   * Update value, because EDS is using checkbox which is based on boolean values, we are using 0 for inactive and 1 for active.
   */
  public getStatusString(status: boolean): string {
    return status ? BasicStatusType.ACTIVE : BasicStatusType.INACTIVE;
  }

}
